import React, { useState, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

// react router
import { Link, useNavigate } from "react-router-dom";

// utilities
import { isEmpty } from "lodash";
import {
  showSuccessSnackbar,
  showErrorSnackbar
} from "../../../snackbar/helpers";

// translations
import { FormattedMessage, injectIntl } from "react-intl";

import { _generateCode } from "../../redux/actions";
import { useSelector } from "react-redux";

import "react-toastify/dist/ReactToastify.css";
import { Form } from "reactstrap";
import Honeypot from "../../../../components/Honeypot";

// form validations
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Fallback values for environment variables
const SITE_KEY = process.env.REACT_APP_SITE_KEY || '6LeFxpEpAAAAADwQ0FRqy_ukCnYxtA23xqgxm-oW' // Default fallback, will be replaced by actual env var in production
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-ffffffffffff' // Test key

// login schema
const schema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required")
})

const PasswordRecovery = () => {
  // ** Hooks
  const navigate = useNavigate()
  const theme = useSelector((state) => state.theme.value)

  // ** States
  const [loading, setLoading] = useState(false)
  const [recaptchaConfirmed, setRecaptchaConfirmed] = useState(false)
  const [recaptchaResponse, setRecaptchaResponse] = useState("")
  const [success, setSuccess] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState("")
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480)
  const [pendingData, setPendingData] = useState(null)
  const captchaRef = useRef(null)
  const onHCaptchaVerify = (token) => {
    setRecaptchaConfirmed(true)
    setRecaptchaResponse(token)
    if (pendingData) {
      performGenerateCode(pendingData, token)
    }
  }

  const onHCaptchaError = (error) => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setLoading(false)
    setPendingData(null) // Clear pending data on error
  }

  const onHCaptchaExpire = () => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setPendingData(null) // Clear pending data on expire
  }

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width <= 768)
      setIsSmallMobile(width <= 480)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // ** Form Validation
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onSubmit"
  })
  const exchangeName = useSelector((state) => state.exchangeCustomization.value.name)

  document.title = `${exchangeName}: Recover Password`

  const onSubmit = (data) => {
    if (data.company_name) {
      return
    }

    if (isEmpty(errors)) {
      setLoading(true)
      setPendingData(data)
      if (captchaRef.current) {
        captchaRef.current.execute()
      } else {
        // Fallback if hCaptcha ref is not available, though it should be
        performGenerateCode(data, "")
      }
    }
  }

  const performGenerateCode = (data, captchaToken) => {
    setSubmittedEmail(data.email)
    _generateCode(
      {
        email: data.email,
        captcha_response: captchaToken
      },
      (data) => {
        setLoading(false)
        setSuccess(true)
        setPendingData(null)
        showSuccessSnackbar({
          alertMessage: "Email with reset code sent successfully"
        })
      },
      (error) => {
        setLoading(false)
        setPendingData(null)
        if (error === 404) {
          showErrorSnackbar({
            alertMessage: "No user with this email found."
          })
        } else {
          showErrorSnackbar({
            alertMessage: error.errors[0].length ? (
              <FormattedMessage id={error.errors[0]} />
            ) : (
              "Unknown Error"
            )
          })
        }
      }
    )
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: isMobile ? 'auto' : '100%'
    }}>
      {/* Mobile Logo/Branding */}
      {isMobile && (
        <div style={{
          textAlign: 'center',
          marginBottom: isSmallMobile ? '30px' : '40px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isSmallMobile ? '8px' : '12px',
            marginBottom: isSmallMobile ? '20px' : '25px',
          }}>
            <img
              src="/logo-small-white.svg"
              alt="Logo"
              width={isSmallMobile ? "28" : "35"}
              height={isSmallMobile ? "28" : "35"}
              style={{ objectFit: 'contain' }}
            />
            <span style={{
              fontSize: isSmallMobile ? '24px' : '28px',
              fontWeight: 700,
              color: 'var(--text-color, white)',
            }}>WealthTech</span>
          </div>
          <p style={{
            color: 'var(--text-secondary, #94a3b8)',
            fontSize: isSmallMobile ? '14px' : '16px',
            lineHeight: 1.5,
            margin: 0,
          }}>
            No worries! Enter your email to reset your password.
          </p>
        </div>
      )}

      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '400px',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: isMobile ? '30px' : '40px' }}>
          <h2 style={{
            fontSize: isMobile ? '28px' : '32px',
            color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
            marginBottom: '10px',
            textAlign: 'center',
            fontWeight: 600,
          }}>Reset Password</h2>
          <p style={{
            color: 'var(--text-secondary, #94a3b8)',
            fontSize: isMobile ? '14px' : '16px',
            textAlign: 'center',
            margin: 0,
          }}>Enter your email to receive a reset link</p>
        </div>

        {success ? (
          <div>
            <div style={{
              padding: isSmallMobile ? '15px' : '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '2px solid rgba(34, 197, 94, 0.3)',
              textAlign: 'center',
            }}>
              <div style={{
                width: isSmallMobile ? '50px' : '60px',
                height: isSmallMobile ? '50px' : '60px',
                background: 'rgba(34, 197, 94, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px',
              }}>
                <svg width={isSmallMobile ? "25" : "30"} height={isSmallMobile ? "25" : "30"} viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h3 style={{
                color: '#86efac',
                fontSize: isSmallMobile ? '16px' : isMobile ? '18px' : '20px',
                fontWeight: 600,
                marginBottom: '10px',
              }}>
                Check Your Email
              </h3>
              <p style={{
                color: '#86efac',
                fontSize: isSmallMobile ? '12px' : isMobile ? '13px' : '14px',
                lineHeight: 1.6,
                margin: 0,
              }}>
                We've sent a password reset link to <strong>{submittedEmail}</strong>
              </p>
            </div>

            <div style={{
              padding: isSmallMobile ? '12px' : '15px',
              background: 'var(--input-background, rgba(30, 50, 70, 0.3))',
              borderRadius: '10px',
              border: '1px solid var(--input-border, rgba(100, 150, 180, 0.2))',
              marginBottom: isSmallMobile ? '15px' : '20px',
            }}>
              <p style={{
                color: 'var(--text-secondary, #cbd5e1)',
                fontSize: isSmallMobile ? '12px' : isMobile ? '13px' : '14px',
                margin: 0,
                lineHeight: 1.6,
              }}>
                <strong>Didn't receive the email?</strong><br />
                Check your spam folder or try resending after a few minutes.
              </p>
            </div>

            <button
              onClick={() => {
                setSuccess(false)
                setSubmittedEmail("")
                setRecaptchaConfirmed(false)
                setRecaptchaResponse("")
              }}
              style={{
                width: '100%',
                padding: isSmallMobile ? '12px' : isMobile ? '14px' : '16px',
                background: 'transparent',
                border: '2px solid #009999',
                borderRadius: '10px',
                color: '#009999',
                fontSize: isSmallMobile ? '14px' : '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '15px',
              }}
            >
              Send Another Email
            </button>
          </div>
        ) : (
          <Form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Honeypot register={register} />
            <div style={{ marginBottom: isMobile ? '20px' : '25px' }}>
              <label htmlFor="email" style={{
                display: 'block',
                color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
              }}>Email Address</label>
              <input
                type="email"
                id="email"
                placeholder="user@example.com"
                {...register("email")}
                style={{
                  width: '100%',
                  padding: isMobile ? '12px 16px' : '14px 16px',
                  background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
                  border: `1px solid ${errors.email ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
                  borderRadius: '8px',
                  color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
                required
              />
              {errors.email && (
                <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.email.message}
                </span>
              )}
            </div>

            {/* hCaptcha (Invisible) */}
            <HCaptcha
              id="hcaptcha-recovery"
              ref={captchaRef}
              size="invisible"
              sitekey={HCAPTCHA_SITE_KEY}
              onVerify={onHCaptchaVerify}
              onError={onHCaptchaError}
              onExpire={onHCaptchaExpire}
              theme={theme}
            />

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 16px',
                background: loading ? 'var(--button-disabled, #6b7280)' : 'linear-gradient(135deg, #009999 0%, #007a7a 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Processing...' : 'Send Reset Link'}
            </button>
          </Form>
        )}

        <div style={{
          textAlign: 'center',
          color: theme === 'light' ? '#6b7280' : 'var(--text-secondary, #94a3b8)',
          fontSize: '14px',
          marginTop: '30px',
        }}>
          Remember your password?{' '}
          <Link to="/login" style={{
            color: '#009999',
            textDecoration: 'none',
            fontWeight: 600,
          }}>
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  )
}
export default injectIntl(PasswordRecovery)
